import type { WeeklyReport } from "@/types/WeeklyReport";

export type ReportStatus = "draft" | "pending" | "sent" | "failed";

/**
 * Resolve the current status of a report based on legacy and new fields.
 * Matches backend logic for backward compatibility.
 *
 * Resolution rules (priority order):
 * 1. If report.status exists: use it
 * 2. If sentByEmail === true: 'sent'
 * 3. If deliveryStatus?.status === 'failed': 'failed'
 * 4. Otherwise: 'draft'
 */
export function resolveReportStatus(report: WeeklyReport): ReportStatus {
  if (report.status) {
    return report.status as ReportStatus;
  }

  if (report.sentByEmail === true) {
    return "sent";
  }

  if (report.deliveryStatus?.status === "failed") {
    return "failed";
  }

  return "draft";
}

/**
 * Check if report should be treated as a draft (editable/resumable)
 */
export function isDraft(report: WeeklyReport): boolean {
  return resolveReportStatus(report) === "draft";
}

/**
 * Check if report has been sent (immutable)
 */
export function isSent(report: WeeklyReport): boolean {
  return resolveReportStatus(report) === "sent";
}

/**
 * Check if report failed to send (can retry)
 */
export function isFailed(report: WeeklyReport): boolean {
  return resolveReportStatus(report) === "failed";
}

/**
 * Get human-readable status label for UI display
 */
export function getStatusLabel(report: WeeklyReport): string {
  const status = resolveReportStatus(report);
  const labels: Record<ReportStatus, string> = {
    draft: "Draft",
    pending: "Pending",
    sent: "Sent",
    failed: "Failed",
  };
  return labels[status] || "Unknown";
}

/**
 * Get status badge color for UI display
 */
export function getStatusColor(
  report: WeeklyReport,
): "gray" | "amber" | "green" | "red" {
  const status = resolveReportStatus(report);
  const colors: Record<ReportStatus, "gray" | "amber" | "green" | "red"> = {
    draft: "gray",
    pending: "amber",
    sent: "green",
    failed: "red",
  };
  return colors[status] || "gray";
}

/**
 * Filter reports by status
 */
export function filterByStatus(
  reports: WeeklyReport[],
  status: ReportStatus,
): WeeklyReport[] {
  return reports.filter((r) => resolveReportStatus(r) === status);
}

/**
 * Filter reports that are drafts (can be resumed/edited)
 */
export function getDrafts(reports: WeeklyReport[]): WeeklyReport[] {
  return filterByStatus(reports, "draft");
}

/**
 * Filter reports that are sent (completed)
 */
export function getSentReports(reports: WeeklyReport[]): WeeklyReport[] {
  return filterByStatus(reports, "sent");
}

/**
 * Sort reports by updatedAt (most recent first)
 */
export function sortByUpdatedAt(reports: WeeklyReport[]): WeeklyReport[] {
  return [...reports].sort((a, b) => {
    const aTime = new Date(a.updatedAt || a.date || 0).getTime();
    const bTime = new Date(b.updatedAt || b.date || 0).getTime();
    return bTime - aTime;
  });
}
